import { useState, useEffect } from 'react'
import axios from 'axios'
import { useStore } from "../contexts/Store.context"
import { useModal } from "../contexts/Modal.context"
import { useMutation, useQueryClient } from 'react-query'
import { useForm } from "react-hook-form"
import { InputWithLabel } from "../components/FormComponents"
import Switch from "../components/Switch"
import Button from '../components/Button'
import Select from "../components/Select"
import Spinner from '../components/Spinner'
import { CgCheckO } from "react-icons/cg"

const intervalOptions = [
    { value: "hourly" }, { value: "daily" }, { value: "weekly" }, { value: "monthly" }, { value: "biannually" }, { value: "annually" }
]

export default function CreatePlan() {
    const queryClient = useQueryClient()
    const { closeModal, notify } = useModal()
    const { currentUser, authorizedCards, setPayWithPlanCode } = useStore()

    const { handleSubmit, control, register, errors } = useForm()
    const [authorizationCode, setAuthorizationCode] = useState(undefined)
    const [send_invoices, setSendInvoices] = useState(true)
    const [send_sms, setSendSMS] = useState(false)

    const { isLoading, mutate, reset } = useMutation(data =>
        axios.post(process.env.REACT_APP_BASE_URL + "subscriptions/new-plan", data),
        {
            onSuccess: (data) => {
                if (!authorizationCode) {
                    /**
                     * First time user. Plan was created but no subscription was made. 
                     * API will create the Plan regardless (change this order to payment first), and return the created Plan object. 
                     * Manual payment has to be made.
                    */
                    const { plan_code } = data.data.data
                    setPayWithPlanCode(plan_code)
                }

                notify(data.data.message)
                const keys = ["customer-data", "plans", `subscriptions - ${currentUser.id}`]
                keys.forEach(key => queryClient.refetchQueries(key))
                reset()
                closeModal()
            },
            onError: (error) => {
                notify("There was an error")
                console.log(error.message)
            }
        }
    )

    const onSubmit = async (values, event) => {
        // Paystact requires: name, amount,interval, description, invoice_limit, send_invoices, send_sms
        event.preventDefault()
        const { name, amount, interval } = values

        const body = {
            name,
            amount: +amount * 100, //is in kobos
            channels: ["card"],
            send_invoices, interval, send_sms,
            email: currentUser.email,
            authorization_code: authorizationCode
        }

        if (!isLoading) mutate(body)
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-b-xl space-y-5 p-6 max-w-2xl relative">
            {isLoading &&
                <div style={{ zIndex: 100 }} className="w-full h-full bg-gray-400 bg-opacity-50 grid place-items-center absolute top-0 left-0">
                    <Spinner color="text-white" scale="w-20 h-20" />
                </div>
            }
            <InputWithLabel
                name="name"
                placeholder="My 2021 Goals"
                register={register}
                errors={errors}
                required
            />
            <InputWithLabel
                name="amount"
                pattern="numeric"
                placeholder="5,000"
                register={register}
                errors={errors}
                required
            />
            <div className="space-y-1">
                <label htmlFor="interval" className="text-gray-600 text-sm capitalize">Interval</label>
                <Select
                    name="interval"
                    register={register}
                    // setValue={setinterval}
                    control={control}
                    initialOptions={intervalOptions}
                    initialValue="weekly"
                    width="w-full"
                />
            </div>
            {
                authorizedCards.length > 1 ?
                    <CardOptions
                        cards={authorizedCards}
                        setValue={setAuthorizationCode}
                    /> : null
            }
            <div className={`flex justify-between space-x-2 px-4 py-2 border rounded-lg items-center`}>
                <label className="text-gray-600 text-sm capitalize">Send Invoices</label>
                <Switch states={[]} setValue={setSendInvoices} initialState={send_invoices} />
            </div>
            <div className={`flex justify-between space-x-2 px-4 py-2 border rounded-lg items-center`}>
                <label className="text-gray-600 text-sm capitalize">Send SMS</label>
                <Switch setValue={setSendSMS} />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-8">
                <Button onClick={closeModal} variant="light-blue" type="reset">Cancel</Button>
                <Button disabled={isLoading} type="submit">{isLoading ? "Processing..." : "Subscribe"}</Button>
            </div>
        </form>
    )
}

function CardOptions({ cards, setValue }) {
    const [index, setIndex] = useState(0)

    useEffect(() => {
        setValue(cards[index]?.authorization_code)
        //eslint-disable-next-line
    }, [index])

    return (
        <div className="space-y-3">
            <p className="capitalize text-gray-500 text-sm">{cards.length > 1 ? "Choose" : ""} payment option</p>
            <div className="space-y-3 p-4 bg-indigo-50 rounded-lg">
                {
                    cards.map((card, i) => {
                        const isSelected = i == index
                        const { brand, exp_month, exp_year, last4 } = card

                        return (
                            <div
                                key={i}
                                onClick={() => setIndex(i)}
                                className={`px-4 py-3 rounded-lg flex items-center space-x-4 cursor-pointer 
                                    ${isSelected ? "bg-indigo-600 text-white shadow-lg" : "bg-gray-50 border"}
                                `}
                            >
                                {isSelected && <CgCheckO className="text-2xl text-white"></CgCheckO>}
                                <p>
                                    <span className="capitalize">{brand}</span> ending in <span className="font-medium">{last4}</span>
                                    <span className="block text-sm">Expires {exp_month}/{exp_year}</span>
                                </p>
                            </div>
                        )
                    })

                }
            </div>
        </div>
    )
}